import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../../contexts/AuthContext';
import { useData } from '../../../contexts/DataContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const { FiSave, FiLayout, FiType, FiEye, FiSmartphone, FiMonitor, FiCheck, FiExternalLink, FiRefreshCw, FiLock, FiShield, FiAward, FiCheckBadge, FiStar, FiBriefcase } = FiIcons;

const QUALIFICATION_ICONS = {
  FiShield: FiShield,
  FiCheckBadge: FiCheckBadge,
  FiStar: FiStar,
  FiAward: FiAward,
  FiBriefcase: FiBriefcase
};

const BADGE_OPTIONS = [
  { id: 'certified_hms', label: 'Sertifisert HMS-gransker', icon: 'FiShield', color: 'blue' },
  { id: 'legal_expert', label: 'Juridisk Spesialist', icon: 'FiCheckBadge', color: 'indigo' },
  { id: 'top_rated', label: 'Toppvurdert 2024', icon: 'FiStar', color: 'amber' },
  { id: 'partner', label: 'Partner', icon: 'FiBriefcase', color: 'slate' },
  { id: 'senior', label: 'Senior Utreder', icon: 'FiAward', color: 'emerald' }
];

const MicrositeEditor = () => {
  const { user } = useAuth();
  const { updateUtreder: updateUtrederProfile, utredere } = useData();
  const currentUtreder = utredere?.find(u => String(u.id) === String(user?.id));
  
  const [activeTab, setActiveTab] = useState('design');
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  
  // State som matcher bedrifts-editoren
  const [branding, setBranding] = useState({
    primaryColor: '#1e3a8a',
    accentColor: '#eff6ff',
    logoUrl: '',
    welcomeTitle: 'Velkommen',
    welcomeMessage: 'Jeg tilbyr uavhengig utredning og bistand i varslingssaker.',
    showLogo: true
  });

  const [formData, setFormData] = useState({
    title_role: '',
    public_contact_email: '',
    public_contact_phone: '',
    bio: '',
    slug: '',
    qualifications: []
  });

  // Load initial data
  useEffect(() => {
    if (currentUtreder) {
      setFormData({
        title_role: currentUtreder.title_role || '',
        public_contact_email: currentUtreder.public_contact_email || '',
        public_contact_phone: currentUtreder.public_contact_phone || '',
        bio: currentUtreder.bio || '',
        slug: currentUtreder.slug || '',
        qualifications: currentUtreder.qualifications || []
      });
      // Load branding if exists, else defaults
      if (currentUtreder.branding) {
        setBranding(prev => ({ ...prev, ...currentUtreder.branding }));
      }
    }
  }, [currentUtreder]);

  const handleBrandingChange = (key, value) => {
    setBranding(prev => ({ ...prev, [key]: value }));
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const toggleQualification = (badge) => {
    const exists = formData.qualifications.find(q => q.id === badge.id);
    if (exists) {
      setFormData({
        ...formData,
        qualifications: formData.qualifications.filter(q => q.id !== badge.id)
      });
    } else {
      setFormData({
        ...formData,
        qualifications: [...formData.qualifications, badge]
      });
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateUtrederProfile(user.id, { 
        ...formData,
        branding 
      });
      setSaveMessage('✅ Lagret og publisert!');
    } catch (error) {
      setSaveMessage('❌ Feil ved lagring');
    } finally {
      setIsSaving(false);
      setTimeout(() => setSaveMessage(''), 3000);
    }
  };

  const colorPresets = [
    { name: 'Standard Blå', primary: '#1e40af', accent: '#eff6ff' },
    { name: 'Havblå', primary: '#0369a1', accent: '#f0f9ff' },
    { name: 'Indigo Drøm', primary: '#4338ca', accent: '#eef2ff' },
    { name: 'Midnattsblå', primary: '#1e3a8a', accent: '#f8fafc' },
    { name: 'Isbre', primary: '#2563eb', accent: '#ffffff' },
    { name: 'Smaragd Grønn', primary: '#059669', accent: '#ecfdf5' },
    { name: 'Teal Hav', primary: '#0d9488', accent: '#f0fdfa' },
    { name: 'Skog', primary: '#166534', accent: '#f0fdf4' },
    { name: 'Oliven', primary: '#3f6212', accent: '#f7fee7' },
    { name: 'Mint', primary: '#0f766e', accent: '#ccfbf1' },
    { name: 'Rust Rød', primary: '#9f1239', accent: '#fff1f2' },
    { name: 'Rose', primary: '#be123c', accent: '#fff1f2' },
    { name: 'Brennende Oransje', primary: '#c2410c', accent: '#fff7ed' },
    { name: 'Korall', primary: '#ea580c', accent: '#fff7ed' },
    { name: 'Kirsebær', primary: '#b91c1c', accent: '#fef2f2' },
    { name: 'Dyp Lilla', primary: '#581c87', accent: '#faf5ff' },
    { name: 'Lavendel', primary: '#7c3aed', accent: '#f5f3ff' },
    { name: 'Fuksia', primary: '#a21caf', accent: '#fdf4ff' },
    { name: 'Mørk Skifer', primary: '#0f172a', accent: '#f1f5f9' },
    { name: 'Kullgrå', primary: '#334155', accent: '#f8fafc' },
    { name: 'Ren Sort', primary: '#000000', accent: '#ffffff' },
    { name: 'Mokka', primary: '#451a03', accent: '#fffbeb' },
    { name: 'Sitrus', primary: '#a16207', accent: '#fefce8' },
    { name: 'Gull', primary: '#854d0e', accent: '#fefce8' },
  ];

  const applyPreset = (preset) => {
    setBranding(prev => ({ ...prev, primaryColor: preset.primary, accentColor: preset.accent }));
  };

  const getContrastColor = (hexColor) => {
    if (!hexColor) return '#111827';
    try {
      const hex = hexColor.replace('#', '');
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
      return (yiq >= 128) ? '#111827' : '#ffffff';
    } catch (e) { return '#111827'; }
  };

  return (
    <div className="min-h-screen bg-[#0f172a] text-slate-200 p-6">
      {/* Top Header */}
      <div className="max-w-[1600px] mx-auto mb-8 bg-[#1e293b] rounded-2xl p-6 border border-slate-700 flex justify-between items-center shadow-lg">
        <div>
          <h2 className="text-2xl font-bold text-white">Microsite-Editor</h2>
          <p className="text-slate-400 text-sm">Tilpass din offentlige utreder-profil.</p>
        </div>
        <div className="flex items-center gap-4">
          <button onClick={() => window.open(`/u/${formData.slug || user?.id}`, '_blank')} className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg flex items-center gap-2 transition-colors border border-slate-600">
            <SafeIcon icon={FiExternalLink} /> Åpne Microsite
          </button>
          <button onClick={handleSave} disabled={isSaving} className="px-6 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-bold flex items-center gap-2 transition-colors shadow-lg shadow-blue-900/20">
            {isSaving ? <SafeIcon icon={FiRefreshCw} className="animate-spin" /> : <SafeIcon icon={FiSave} />} 
            {saveMessage || 'Lagre & Publiser'}
          </button>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto grid grid-cols-1 xl:grid-cols-12 gap-8">
        
        {/* LEFT COLUMN: EDITOR */}
        <div className="xl:col-span-7 space-y-6">
          <div className="bg-[#1e293b] rounded-2xl border border-slate-700 overflow-hidden flex flex-col h-[750px]">
            {/* Tabs */}
            <div className="flex border-b border-slate-700 bg-slate-800/50">
              <button 
                onClick={() => setActiveTab('design')} 
                className={`flex-1 py-4 text-sm font-bold flex items-center justify-center gap-2 border-b-2 transition-all ${activeTab === 'design' ? 'border-blue-500 text-blue-400 bg-[#1e293b]' : 'border-transparent text-slate-400 hover:text-slate-200'}`}
              >
                <SafeIcon icon={FiLayout} /> Design & Logo
              </button>
              <button 
                onClick={() => setActiveTab('content')} 
                className={`flex-1 py-4 text-sm font-bold flex items-center justify-center gap-2 border-b-2 transition-all ${activeTab === 'content' ? 'border-blue-500 text-blue-400 bg-[#1e293b]' : 'border-transparent text-slate-400 hover:text-slate-200'}`}
              >
                <SafeIcon icon={FiType} /> Innhold & Info
              </button>
            </div>

            {/* Content Area */}
            <div className="p-8 flex-1 overflow-y-auto custom-scrollbar">
              {activeTab === 'design' && (
                <div className="space-y-8">
                  <section>
                    <label className="block text-xs font-black text-slate-400 uppercase mb-4 tracking-wider">Velg Tema (24 Varianter)</label>
                    <div className="grid grid-cols-4 gap-3">
                      {colorPresets.map((preset, i) => (
                        <button key={i} onClick={() => applyPreset(preset)} className={`p-2 border rounded-xl hover:scale-105 transition-all text-center group ${branding.primaryColor === preset.primary ? 'border-blue-500 ring-2 ring-blue-500/20 bg-slate-800' : 'border-slate-700 bg-slate-800/50'}`}>
                          <div className="flex h-8 w-full rounded-md mb-2 overflow-hidden border border-slate-600">
                            <div className="w-1/2 h-full" style={{ backgroundColor: preset.primary }}></div>
                            <div className="w-1/2 h-full" style={{ backgroundColor: preset.accent }}></div>
                          </div>
                          <span className="text-[10px] text-slate-400 group-hover:text-white font-medium">{preset.name}</span>
                        </button>
                      ))}
                    </div>
                  </section>
                  
                  <section className="bg-slate-800/50 p-6 rounded-xl border border-slate-700">
                    <label className="block text-xs font-black text-slate-400 uppercase mb-4">Profilbilde / Logo</label>
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-xl bg-white flex items-center justify-center border-2 border-slate-600 overflow-hidden">
                        {branding.logoUrl ? (
                          <img src={branding.logoUrl} alt="Logo" className="w-full h-full object-cover" />
                        ) : (
                          <SafeIcon icon={FiEye} className="text-slate-300 text-2xl" />
                        )}
                      </div>
                      <div className="flex-1">
                        <input 
                          type="text" 
                          placeholder="Lim inn bilde-URL..." 
                          value={branding.logoUrl} 
                          onChange={e => handleBrandingChange('logoUrl', e.target.value)}
                          className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-2 text-sm text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                        />
                      </div>
                    </div>
                  </section>

                  <section className="bg-slate-800/50 p-6 rounded-xl border border-slate-700">
                    <label className="block text-xs font-black text-slate-400 uppercase mb-3">Tilpassede Farger</label>
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <span className="text-xs text-slate-400 block mb-2">Hovedfarge</span>
                        <div className="flex items-center gap-3">
                          <input type="color" value={branding.primaryColor} onChange={e => handleBrandingChange('primaryColor', e.target.value)} className="w-10 h-10 rounded cursor-pointer bg-transparent border-0" />
                          <span className="text-sm font-mono text-slate-300">{branding.primaryColor}</span>
                        </div>
                      </div>
                      <div>
                        <span className="text-xs text-slate-400 block mb-2">Bakgrunnsfarge</span>
                        <div className="flex items-center gap-3">
                          <input type="color" value={branding.accentColor} onChange={e => handleBrandingChange('accentColor', e.target.value)} className="w-10 h-10 rounded cursor-pointer bg-transparent border-0" />
                          <span className="text-sm font-mono text-slate-300">{branding.accentColor}</span>
                        </div>
                      </div>
                    </div>
                  </section>
                </div>
              )}

              {activeTab === 'content' && (
                <div className="space-y-6">
                  {/* Badge Selection UI */}
                  <div>
                    <label className="block text-xs font-black text-slate-400 uppercase mb-4 tracking-wider">Kvalifikasjons-Badges</label>
                    <div className="grid grid-cols-2 gap-2">
                      {BADGE_OPTIONS.map(badge => {
                        const isSelected = formData.qualifications.some(q => q.id === badge.id);
                        return (
                          <button
                            key={badge.id}
                            onClick={() => toggleQualification(badge)}
                            className={`flex items-center gap-3 p-3 rounded-xl border text-left transition-all
                              ${isSelected ? 'bg-blue-900/40 border-blue-500 ring-1 ring-blue-500/20' : 'bg-slate-800/50 border-slate-700 hover:border-slate-500'}`}
                          >
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-lg
                              ${isSelected ? 'bg-blue-500 text-white' : 'bg-slate-700 text-slate-400'}`}>
                              <SafeIcon icon={QUALIFICATION_ICONS[badge.icon]} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-[11px] font-bold truncate ${isSelected ? 'text-white' : 'text-slate-400'}`}>{badge.label}</p>
                            </div>
                            {isSelected && <SafeIcon icon={FiCheck} className="text-blue-400 flex-shrink-0" />}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-black text-slate-400 uppercase mb-2">Velkomsttittel</label>
                    <input 
                      type="text" 
                      value={branding.welcomeTitle} 
                      onChange={e => handleBrandingChange('welcomeTitle', e.target.value)} 
                      className="w-full bg-slate-900 border border-slate-600 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-black text-slate-400 uppercase mb-2">Velkomstmelding / Intro</label>
                    <textarea 
                      rows={4} 
                      value={branding.welcomeMessage} 
                      onChange={e => handleBrandingChange('welcomeMessage', e.target.value)} 
                      className="w-full bg-slate-900 border border-slate-600 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-6 pt-4 border-t border-slate-700">
                    <div>
                      <label className="block text-xs font-black text-slate-400 uppercase mb-2">Ditt Navn / Firma</label>
                      <input 
                        type="text" 
                        value={user?.name} 
                        readOnly
                        className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-slate-400 cursor-not-allowed"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-black text-slate-400 uppercase mb-2">Tittel / Rolle</label>
                      <input 
                        type="text" 
                        name="title_role"
                        value={formData.title_role} 
                        onChange={handleFormChange}
                        className="w-full bg-slate-900 border border-slate-600 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 outline-none"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block text-xs font-black text-slate-400 uppercase mb-2">Offentlig E-post</label>
                      <input 
                        type="email" 
                        name="public_contact_email"
                        value={formData.public_contact_email} 
                        onChange={handleFormChange}
                        className="w-full bg-slate-900 border border-slate-600 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-black text-slate-400 uppercase mb-2">Offentlig Telefon</label>
                      <input 
                        type="tel" 
                        name="public_contact_phone"
                        value={formData.public_contact_phone} 
                        onChange={handleFormChange}
                        className="w-full bg-slate-900 border border-slate-600 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 outline-none"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN: PREVIEW */}
        <div className="xl:col-span-5 flex justify-center items-start pt-8">
          <div className="sticky top-24">
            <h3 className="text-xs font-black text-slate-500 uppercase tracking-widest mb-4 text-center">Live Forhåndsvisning</h3>
            <div className="bg-white shadow-[0_35px_60px_-15px_rgba(0,0,0,0.5)] border-[10px] border-[#1e293b] rounded-[40px] overflow-hidden h-[700px] w-[360px] relative mx-auto ring-1 ring-slate-700">
              <div className="h-6 bg-white w-full absolute top-0 z-20 flex justify-between px-6 items-center text-[10px] font-bold text-gray-400 border-b border-gray-100">
                <span>09:41</span>
                <div className="flex gap-1">
                  <div className="w-3 h-3 bg-gray-200 rounded-full"></div>
                  <div className="w-3 h-3 bg-gray-200 rounded-full"></div>
                </div>
              </div>
              
              <div className="h-10 bg-gray-50 border-b border-gray-200 w-full absolute top-6 z-20 flex items-center justify-center px-4">
                <div className="bg-white border border-gray-200 rounded-full w-full h-6 flex items-center justify-center text-[8px] text-gray-400">
                  <SafeIcon icon={FiLock} className="mr-1 text-[8px]" /> hmsvarsling.no/u/{formData.slug || 'din-profil'}
                </div>
              </div>

              <div className="h-full overflow-y-auto bg-gray-50 pt-16 pb-8">
                <div className="pb-12 pt-10 px-6 text-center relative" style={{ backgroundColor: branding.accentColor }}>
                  {/* Badges in Preview */}
                  <div className="flex flex-wrap justify-center gap-1 mb-2 relative z-10">
                    {formData.qualifications?.map((q, i) => (
                      <div key={i} className="bg-white/20 backdrop-blur-sm p-1 rounded-full text-[8px] text-white">
                        <SafeIcon icon={QUALIFICATION_ICONS[q.icon] || FiAward} />
                      </div>
                    ))}
                  </div>
                  
                  <div className="w-24 h-24 bg-white rounded-3xl shadow-xl mx-auto mb-4 flex items-center justify-center border-4 border-white relative z-10 overflow-hidden">
                    {branding.logoUrl ? (
                      <img src={branding.logoUrl} alt="Logo" className="w-full h-full object-cover" />
                    ) : (
                      <span className="font-black text-4xl" style={{ color: branding.primaryColor }}>{user?.name?.substring(0, 1)}</span>
                    )}
                  </div>
                  <h1 className="text-xl font-black relative z-10 mb-1" style={{ color: getContrastColor(branding.accentColor) }}>
                    {branding.welcomeTitle}
                  </h1>
                  <p className="text-xs font-medium opacity-80" style={{ color: getContrastColor(branding.accentColor) }}>
                    {formData.title_role || 'Uavhengig Utreder'}
                  </p>
                </div>
                
                <div className="px-6 -mt-6 relative z-20 space-y-4">
                  <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100 text-center">
                    <p className="text-sm text-gray-600 leading-relaxed">{branding.welcomeMessage}</p>
                  </div>
                  
                  <button className="w-full p-4 rounded-xl shadow-lg text-white font-black uppercase text-xs tracking-wider flex items-center justify-center gap-2 transition-transform active:scale-95" style={{ backgroundColor: branding.primaryColor }}>
                    <SafeIcon icon={FiSmartphone} /> Ta Kontakt
                  </button>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 text-center">
                      <SafeIcon icon={FiCheck} className="mx-auto mb-2 text-green-500" />
                      <p className="text-[10px] font-bold text-gray-400 uppercase">Erfaring</p>
                      <p className="text-xs font-bold text-gray-800">Sertifisert</p>
                    </div>
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 text-center">
                      <SafeIcon icon={FiShield} className="mx-auto mb-2 text-blue-500" />
                      <p className="text-[10px] font-bold text-gray-400 uppercase">Sikkerhet</p>
                      <p className="text-xs font-bold text-gray-800">Taushetsplikt</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 w-32 h-1 bg-gray-300 rounded-full z-30"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MicrositeEditor;